import React from 'react';
import { Collapse } from '@kite-js/core';
import { Box, Button, GridItem } from '@drone-js/core';
import OfferCard from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCard/OfferCard';
import { connect } from 'react-redux';
import { StringFormatter } from '@agoda/react-handyman';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesPackageOptionShowMoreButtonClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesPackageOptionShowMoreButtonClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/detail/PackageOptions/PackageOptionContainer/PackageOption.selectors';
import { getIsQueryParamEnrichedOfferExist } from 'component/page/detail/PackageOptions/PackageOptionsUtils';
import { useLocation } from 'react-router-dom';
import { getEnrichedOfferIdFromQueryParams } from 'gql/service/graph/activityService';

const PackageOption: React.FC<Props> = (props) => {
    const {
        offers,
        supplier,
        cms,
        selectedOffer,
        resetSelectedOfferCard,
        isShowDateNotAvailableNotification,
        setShowContinueYourBookingNotification,
        isContinueYourBooking,
        isDetailPageForSimCardEnabled,
    } = props;
    const [isCollapsed, toggleCollapse] = React.useState(true);
    const [isFirstLanding, setIsFirstLanding] = React.useState(true);
    const locationSearch = useLocation().search;
    const enrichedOfferIdFromQueryParams = getEnrichedOfferIdFromQueryParams(locationSearch);

    const onCollapseButtonClick = React.useCallback(() => {
        toggleCollapse(!isCollapsed);
        trackElementEvent<ActivitiesPackageOptionShowMoreButtonClickEventActivitiesFieldsV1>(
            ActivitiesPackageOptionShowMoreButtonClickEventActivitiesManifestV1.ids,
            {},
            { action_element_value: isCollapsed ? 'show_more' : 'show_less' },
        );
        if (selectedOffer && selectedOffer.offerIndex !== undefined && selectedOffer.offerIndex >= 5) {
            resetSelectedOfferCard();
        }
    }, [isCollapsed, resetSelectedOfferCard, selectedOffer]);

    React.useEffect(() => {
        if (isShowDateNotAvailableNotification) {
            setShowContinueYourBookingNotification(cms[371434]);
        }
    }, [cms, isShowDateNotAvailableNotification, setShowContinueYourBookingNotification]);

    React.useEffect(() => {
        if (isFirstLanding && isContinueYourBooking && enrichedOfferIdFromQueryParams) {
            const isEnrichedOfferExist = getIsQueryParamEnrichedOfferExist(offers, enrichedOfferIdFromQueryParams);
            if (!isEnrichedOfferExist) {
                setShowContinueYourBookingNotification(cms[371433]);
            }
            setIsFirstLanding(false);
        }
    }, [cms, enrichedOfferIdFromQueryParams, isContinueYourBooking, isFirstLanding, offers, setShowContinueYourBookingNotification]);

    return (
        <Box data-testid="details-available-package-option-container" px={isDetailPageForSimCardEnabled ? '0' : '8'} width="100%">
            <GridItem sm={12} key="ResultOptions" data-testid="details-available-package-option">
                {offers.length > 5 ? (
                    <>
                        {offers.slice(0, 5).map((offer, index) => (
                            <OfferCard key={index} offer={offer} index={index} isUnavailableOffer={!offer.isAvailable} supplier={supplier} offerLength={offers.length} />
                        ))}
                        <Collapse
                            data-testid="details-available-package-option-more-options"
                            in={!isCollapsed}
                            hideFully={isCollapsed}
                            timeout={500}
                            style={{ marginTop: '-8px' }}
                            aria-label={cms[112003]}
                        >
                            {offers.slice(5).map((offer, index) => (
                                <OfferCard
                                    key={index + 5}
                                    offer={offer}
                                    index={index + 5}
                                    isUnavailableOffer={!offer.isAvailable}
                                    supplier={supplier}
                                    offerLength={offers.length}
                                />
                            ))}
                        </Collapse>
                        <Button
                            data-testid={`details-available-package-option-show-${isCollapsed ? 'more' : 'less'}-button`}
                            size={props.updateShowMoreButton ? 'm' : 's'}
                            variant="label"
                            hierarchy={props.updateShowMoreButton ? 'tertiary' : 'secondary'}
                            leadingIcon={props.updateShowMoreButton ? undefined : isCollapsed ? 'fill.arrow.thin-dropdown' : 'fill.arrow.thin-collapse'}
                            mt={isCollapsed ? '12' : '0'}
                            width="parent-fit"
                            onClick={onCollapseButtonClick}
                            aria-label={isCollapsed ? StringFormatter.format(cms[239087], offers.slice(5).length).toString() : cms[239089]}
                            label={isCollapsed ? StringFormatter.format(cms[239087], offers.slice(5).length).toString() : cms[239089]}
                        />
                    </>
                ) : (
                    offers.map((offer, index) => (
                        <OfferCard key={index} offer={offer} index={index} isUnavailableOffer={!offer.isAvailable} supplier={supplier} offerLength={offers.length} />
                    ))
                )}
            </GridItem>
        </Box>
    );
};
PackageOption.displayName = 'PackageOptions';

export default connect(mapStateToProps, mapDispatchToProps)(PackageOption);
